import React, { useEffect, useRef, useState } from 'react';
import { Download, RotateCcw } from 'lucide-react';
import dayjs from 'dayjs';
import DateLogic from '../../utils/DateLogic';
import '../../CSS/Photobooth.css';

/* ─── Real Photobooth Strip Dimensions (Vertical 3-Grid with Landscape Photos) ─── */
const STRIP_WIDTH = 600;                     // px – final PNG width
const PADDING = 36;                          // px – side margins
const FRAME_W = STRIP_WIDTH - (PADDING * 2); // 528px photo width
const FRAME_H = Math.round(FRAME_W * (3 / 4)); // 396px photo height (4:3 LANDSCAPE)
const HEADER_H = 56;                         // px – top header
const FOOTER_H = 240;                        // px – card-branded bottom footer
const PHOTO_GAP = 20;                        // px – gap between photos

const STRIP_TOTAL_H =
  HEADER_H +
  FRAME_H * 3 +
  PHOTO_GAP * 2 +
  FOOTER_H; // 56 + 1188 + 40 + 240 = 1524px (classic vertical strip)

/**
 * Load an image into an HTMLImageElement.
 */
const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    if (typeof src === 'string' && !src.startsWith('data:')) {
      img.crossOrigin = 'anonymous';
    }
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

/**
 * Draw rounded rectangle helper.
 */
function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

const PhotoboothStrip = ({ photos, cardData, onRetake }) => {
  const canvasRef = useRef(null);
  const downloadBlobRef = useRef(null);
  const [isRendering, setIsRendering] = useState(true);

  const themeColour = cardData?.themeColour || '#45c0c0';
  const baseCardColour = cardData?.themeBaseCardColour || '#ffffff';
  const eventType = cardData?.eventType || 'Majlis Perkahwinan';
  const greetingName = cardData?.greetingName || '';
  const eventDate = cardData?.eventDate
    ? dayjs(cardData.eventDate).format('D MMMM YYYY')
    : '';
  const cardBackground = cardData?.cardBackground || null;

  /* ── Render canvas ──────────────────────────────────────── */
  useEffect(() => {
    if (!photos || photos.length < 3) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = STRIP_WIDTH;
    canvas.height = STRIP_TOTAL_H;
    const ctx = canvas.getContext('2d');

    const draw = async () => {
      setIsRendering(true);
      downloadBlobRef.current = null;
      // Load all frames before drawing so download never sees a partial strip.
      const loadedPhotos = await Promise.all(
        photos.slice(0, 3).map(async (photo) => {
          try {
            return await loadImage(photo);
          } catch {
            return null;
          }
        }),
      );
      /* ── 1. Base Strip Background ── */
      ctx.fillStyle = baseCardColour;
      ctx.fillRect(0, 0, STRIP_WIDTH, STRIP_TOTAL_H);

      // Card background image if present (softly blended)
      if (cardBackground) {
        try {
          const bgImg = await loadImage(cardBackground);
          ctx.save();
          ctx.globalAlpha = 0.28;
          ctx.drawImage(bgImg, 0, 0, STRIP_WIDTH, STRIP_TOTAL_H);
          ctx.restore();
        } catch {
          /* ignore */
        }
      }

      /* ── Subtle Outer Border for realistic photobooth paper feel ── */
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.08)';
      ctx.lineWidth = 2;
      ctx.strokeRect(1, 1, STRIP_WIDTH - 2, STRIP_TOTAL_H - 2);

      console.log('PhotoboothStrip: total photos received:', photos?.length, photos);
      /* ── 3. Vertical 3-Grid Photo Cells (Landscape 4:3 Photos) ── */
      for (let i = 0; i < 3; i++) {
        const y = HEADER_H + i * (FRAME_H + PHOTO_GAP);
        const x = PADDING;
        const w = FRAME_W;
        const h = FRAME_H;

        // Photo card shadow & white matting border
        ctx.save();
        ctx.shadowColor = 'rgba(0, 0, 0, 0.12)';
        ctx.shadowBlur = 10;
        ctx.shadowOffsetY = 3;
        ctx.fillStyle = '#ffffff';
        roundRect(ctx, x, y, w, h, 12);
        ctx.fill();
        ctx.restore();

        // Photo clipping
        ctx.save();
        roundRect(ctx, x, y, w, h, 12);
        ctx.clip();

        const photoImg = loadedPhotos[i];
        if (photoImg) {
          // Object-fit: cover into 4:3 landscape frame
          const srcAspect = photoImg.width / photoImg.height;
          const dstAspect = w / h; // 528 / 396 = 4:3 (1.333)
          let sx = 0, sy = 0, sw = photoImg.width, sh = photoImg.height;
          if (srcAspect > dstAspect) {
            sw = photoImg.height * dstAspect;
            sx = (photoImg.width - sw) / 2;
          } else {
            sh = photoImg.width / dstAspect;
            sy = (photoImg.height - sh) / 2;
          }
          ctx.drawImage(photoImg, sx, sy, sw, sh, x, y, w, h);
        } else {
          ctx.fillStyle = '#f0f0f0';
          ctx.fillRect(x, y, w, h);
        }
        ctx.restore();

        // Subtle crisp frame border
        ctx.save();
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.7)';
        ctx.lineWidth = 3;
        roundRect(ctx, x + 1.5, y + 1.5, w - 3, h - 3, 11);
        ctx.stroke();

        ctx.strokeStyle = 'rgba(0, 0, 0, 0.08)';
        ctx.lineWidth = 1;
        roundRect(ctx, x, y, w, h, 12);
        ctx.stroke();
        ctx.restore();
      }

      /* ── 4. Card-Branded Footer ── */
      const footerStartY = HEADER_H + 3 * FRAME_H + 2 * PHOTO_GAP + 20;

      // Decorative divider
      ctx.save();
      ctx.strokeStyle = themeColour;
      ctx.globalAlpha = 0.35;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(STRIP_WIDTH / 2 - 70, footerStartY);
      ctx.lineTo(STRIP_WIDTH / 2 + 70, footerStartY);
      ctx.stroke();
      ctx.restore();

      // Heart icon in center of divider
      ctx.save();
      ctx.fillStyle = themeColour;
      ctx.font = '15px "Arial", sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('♥', STRIP_WIDTH / 2, footerStartY + 5);
      ctx.restore();

      // Event Type (styled to match date color: #555555)
      if (eventType) {
        ctx.save();
        ctx.textAlign = 'center';
        ctx.fillStyle = '#555555';
        ctx.font = '600 14px "Inter", "Arial", sans-serif';
        ctx.letterSpacing = '3px';
        ctx.fillText(eventType.toUpperCase(), STRIP_WIDTH / 2, footerStartY + 36, STRIP_WIDTH - 60);
        ctx.restore();
      }

      // Couple / Greeting Name (Serif, romantic wedding typography)
      ctx.save();
      ctx.textAlign = 'center';
      if (greetingName) {
        ctx.fillStyle = themeColour;
        ctx.font = 'bold 32px "Georgia", "Playfair Display", serif';
        ctx.fillText(greetingName, STRIP_WIDTH / 2, footerStartY + 76, STRIP_WIDTH - 60);
      }

      // Event Date
      if (eventDate) {
        ctx.font = '500 16px "Arial", sans-serif';
        ctx.fillStyle = '#555555';
        ctx.fillText(DateLogic.toDay(dayjs(eventDate).format('dddd'), cardData?.language) + ', ' + dayjs(eventDate).format('D MMMM YYYY'), STRIP_WIDTH / 2, footerStartY + 112);
      }

      // Digital Photobooth branding
      ctx.font = '600 12px "Inter", "Arial", sans-serif';
      ctx.fillStyle = themeColour;
      ctx.letterSpacing = '2px';
      ctx.fillText('DIGITAL PHOTOBOOTH', STRIP_WIDTH / 2, footerStartY + 152);

      ctx.font = '12px "Arial", sans-serif';
      ctx.fillStyle = '#888888';
      ctx.fillText('www.sejiwa-my.com', STRIP_WIDTH / 2, footerStartY + 174);
      ctx.restore();
      canvas.toBlob((blob) => {
        downloadBlobRef.current = blob;
        setIsRendering(false);
      }, 'image/png');
    };

    draw().catch((error) => {
      console.error('Unable to render photobooth strip:', error);
      setIsRendering(false);
    });
  }, [photos, cardData, themeColour, baseCardColour, eventType, greetingName, eventDate, cardBackground]);

  /* ── Download PNG ──────────────────────────────────────── */
  const handleDownload = async () => {
    const canvas = canvasRef.current;
    const imageBlob = downloadBlobRef.current;
    if (!canvas || !imageBlob || isRendering) return;

    const fileName = `photobooth-${greetingName.replace(/\s+/g, '-') || 'strip'}.png`;

    // iPhone Safari does not reliably honour an anchor's download attribute.
    // Use the native share sheet there, which lets guests save directly to Photos.
    if (navigator.share && typeof File !== 'undefined') {
      const imageFile = new File([imageBlob], fileName, { type: 'image/png' });
      const shareData = { files: [imageFile], title: 'Photobooth' };
      if (!navigator.canShare || navigator.canShare(shareData)) {
        try {
          await navigator.share(shareData);
          return;
        } catch (error) {
          // Closing the share sheet is intentional. For other errors, use the
          // browser download fallback below.
          if (error?.name === 'AbortError') return;
        }
      }
    }

    const imageUrl = URL.createObjectURL(imageBlob);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
      || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    // Older iOS versions may not support sharing files. Opening the PNG lets
    // the guest use Safari's long-press "Save to Photos" fallback.
    if (isIOS) {
      window.open(imageUrl, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => URL.revokeObjectURL(imageUrl), 60000);
      return;
    }

    const link = document.createElement('a');
    link.download = fileName;
    link.href = imageUrl;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(imageUrl), 1000);
  };

  return (
    <div className="pb-strip-wrap">

      <div className="pb-canvas-container">
        <canvas ref={canvasRef} className="pb-canvas" />
      </div>

      <div className="pb-strip-actions">
        <button className="pb-download-btn" onClick={handleDownload} disabled={isRendering}>
          <Download size={18} strokeWidth={2.2} />
          <span>{isRendering ? 'Processing...' : 'Download'}</span>
        </button>
        <button className="pb-strip-retake-btn" onClick={onRetake}>
          <RotateCcw size={16} strokeWidth={2} />
          <span>Retake</span>
        </button>
      </div>
    </div>
  );
};

export default PhotoboothStrip;
